import React, { useState } from 'react';
import { Box, FormControl, Input, VStack } from 'native-base';
import { useGeneralStyles } from '~/shared/styles';
import { useSpringBigStore, useCreateGiftCardPayment, observer, i18nFn } from '@springbig/shared';
import { Caption, CaptionError } from '~/shared/Typography';
import ButtonSpinner from '~/shared/ButtonSpinner';
import Button from '~/shared/Button';

const AddBank = ({ t }: { t: i18nFn }) => {

    const { member } = useSpringBigStore();
    const gStyle = useGeneralStyles();

    const {
        mutate: createGiftCardPayment,
        isLoading
    } = useCreateGiftCardPayment();

    const [accountName, setAccountName] = useState('');
    const [accountNumber, setAccountNumber] = useState('');
    const [routingNumber, setRoutingNumber] = useState('');
    const [isValidAccountName, setIsValidAccountName] = useState(false);
    const [isValidAccountNumber, setIsValidAccountNumber] = useState(false);
    const [isValidRoutingNumber, setIsValidRoutingNumber] = useState(false);

    const accountNumberPattern = /^\d{5,}$/;
    const routingNumberPattern = /^\d{9}$/;

    const handleAccountNameChange = (text: string) => {

        setAccountName(text);
        setIsValidAccountName(text.trim() !== '');
    };

    const handleAccountNumberChange = (text: string) => {

        setAccountNumber(text);
        setIsValidAccountNumber(accountNumberPattern.test(text));
    };

    const handleRoutingNumberChange = (text: string) => {

        setRoutingNumber(text);
        setIsValidRoutingNumber(routingNumberPattern.test(text));
    };

    const isSubmitEnabled = isValidAccountName && isValidAccountName && isValidRoutingNumber && !isLoading;

    const handleSubmit = () => {

        if (isSubmitEnabled) {
            member && createGiftCardPayment({
                member_id: member?.id.toString() || '',
                account_name: accountName,
                routing_number: routingNumber,
                account_number: accountNumber,
                payment_type: 'check'
            }, {
                onSuccess: (_) => {

                    observer.emit('giftcard-back');
                    observer.emit('giftcard-back');
                }
            });
        }
    };

    return (
        <VStack w="85%" space={4}>
            <Box alignItems={'center'}>
                <Caption fontSize={24}>
                    { t('general.add')} {t('general.payment') }
                </Caption>
                <Caption fontSize={22} fontWeight={'200'}>
                    { t('giftCard.addPayment.addBank.caption') }
                </Caption>
            </Box>
            <FormControl isRequired>
                <Caption fontSize={18} fontWeight={'200'} mb={1}>
                    { t('giftCard.addPayment.addBank.form.name.title') }
                </Caption>
                <Input
                    borderColor={gStyle.button_background}
                    placeholder={t('giftCard.addPayment.addBank.form.name.title')}
                    onChangeText={handleAccountNameChange}
                    value={accountName}
                    returnKeyType='done'
                />
                { !isValidAccountName && accountName &&
                    <CaptionError>
                        { t('giftCard.addPayment.addBank.form.name.error') }
                    </CaptionError>
                }
            </FormControl>

            <FormControl isRequired>
                <Caption fontSize={18} fontWeight={'200'} mb={1}>
                    { t('giftCard.addPayment.addBank.form.number.title') }
                </Caption>
                <Input
                    borderColor={gStyle.button_background}
                    placeholder={t('giftCard.addPayment.addBank.form.number.title')}
                    onChangeText={handleAccountNumberChange}
                    value={accountNumber}
                    keyboardType="numeric"
                    returnKeyType='done'
                />
                { !isValidAccountNumber && accountNumber &&
                    <CaptionError text='giftCard.addPayment.addBank.form.number.error'/>
                }
            </FormControl>

            <FormControl isRequired>
                <Caption fontSize={18} fontWeight={'200'} mb={1}>
                    { t('giftCard.addPayment.addBank.form.routingNumber.title') }
                </Caption>
                <Input
                    borderColor={gStyle.button_background}
                    placeholder={t('giftCard.addPayment.addBank.form.routingNumber.title')}
                    onChangeText={handleRoutingNumberChange}
                    value={routingNumber}
                    keyboardType="numeric"
                    maxLength={9}
                    returnKeyType='done'
                />
                { !isValidRoutingNumber && routingNumber &&
                    <CaptionError>
                        { t('giftCard.addPayment.addBank.form.routingNumber.error') }
                    </CaptionError>
                }
            </FormControl>

            <Button
                onPress={handleSubmit}
                backgroundColor={gStyle.button_background}
                width='55%'
                alignSelf={'center'}
                isDisabled={!isSubmitEnabled}
            >
                { isLoading ?
                    <ButtonSpinner />
                    :
                    <Caption
                        color={gStyle.button_text}
                        fontSize={20}
                    >
                        { t('general.submit') }
                    </Caption>
                }
            </Button>
        </VStack>
    );
};

export default AddBank;
